/**
 * T. Rex — „Jurassic Park: Dr. Sattler vs. T. Rex".
 *
 * VEĽKÁ FIGÚRKA: útočí aj na 2 priestory (ponad figúrky) a nesmie používať
 * tajné chodby (`LARGE_FIGHTERS` v movement.ts). Na konci ťahu si potiahne
 * kartu. Veľa kariet ju za silu zraňuje.
 *
 * Zjednodušenie: fyzický podstavec, ktorý na doske niekedy presahuje aj na
 * susedné pole („chvost"), engine nemodeluje — T. Rex stojí na jednom priestore.
 */

import { neighbors, shareZone } from '../map.js';
import type { Ctx } from '../ops.js';
import { askChoice, dealDamage, discardRandom, drawCard, emit, gainAction, getMap, returnFromDiscard } from '../ops.js';
import { areEnemies } from '../players.js';
import type { PlayerIndex } from '../types.js';
import { draw, momentousShift } from './common.js';
import { askBoost, askMove } from './helpers.js';
import type { CardEffect, EffectCtx, EffectRegistry, HeroSpecial } from './types.js';

const TREX = 't-rex';
const GUT = 't-rex.65-million-years-of-gut-instinct';
const RIPPLES = 't-rex.ripples-in-the-water';
const ANGRY = 't-rex.youre-just-making-her-angry';

const rexOf = (ctx: Ctx, player: PlayerIndex) =>
  ctx.state.fighters.find((f) => f.owner === player && f.defId === TREX) ?? null;

/** T. Rex si sama spôsobí poškodenie („take N damage"). */
function hurtRex(e: EffectCtx, amount: number): void {
  const rex = rexOf(e.ctx, e.player);
  if (rex) dealDamage(e.ctx, rex.id, amount);
}

/** „Move your fighter up to 1 space. Draw 1 card. Gain 1 action." */
export const closerThanSheAppears: CardEffect = {
  scheme: (e) => {
    if (e.fighterId) askMove(e, { fighters: [e.fighterId], max: 1, label: 'Closer Than She Appears: presuň T. Rexa o 1' });
    drawCard(e.ctx, e.player);
    gainAction(e.ctx, e.player);
  },
};

/** „DURING: You may BOOST this card. AFTER: If you won, deal 1 damage to each opposing fighter in T-Rex's zone." */
const thrash: CardEffect = {
  duringChoice: (e) => askBoost(e, 'Thrash: smieš boostovať túto kartu'),
  after: (e) => {
    const rex = rexOf(e.ctx, e.player);
    if (!e.won || !rex) return;
    const map = getMap(e.ctx);
    const targets = e.ctx.state.fighters
      .filter((f) => areEnemies(e.ctx.state, f.owner, e.player) && shareZone(map, f.spaceId, rex.spaceId))
      .map((f) => f.id);
    for (const id of targets) dealDamage(e.ctx, id, 1);
  },
};

/** „Deal 3 damage to the opposing fighter. Then, take 3 damage." */
const recklessLunge: CardEffect = {
  after: (e) => {
    if (e.opponentFighterId) dealDamage(e.ctx, e.opponentFighterId, 3);
    hurtRex(e, 3);
  },
};

/** „DURING: You may BOOST this card. AFTER: If you won the combat, draw 1 card, gain 1 action, and take 2 damage." */
const whenDinosaursRuled: CardEffect = {
  duringChoice: (e) => askBoost(e, 'When Dinosaurs Ruled the Earth: smieš boostovať'),
  after: (e) => {
    if (!e.won) return;
    drawCard(e.ctx, e.player);
    gainAction(e.ctx, e.player);
    hurtRex(e, 2);
  },
};

/** „Choose a card in your discard pile other than „65 Million Years of Gut Instinct" and return it to your hand." */
const gutInstinct: CardEffect = {
  scheme: (e) => {
    const discard = e.ctx.state.players[e.player]?.discard ?? [];
    const cards = discard.filter((id) => e.ctx.state.cards[id]?.defId !== GUT);
    if (cards.length === 0) return;
    askChoice(e.ctx, {
      kind: 'card',
      player: e.player,
      source: e.cardId,
      label: '65 Million Years of Gut Instinct: vezmi si kartu z odhodu do ruky',
      optional: false,
      from: 'discard',
      cards,
      purpose: 'toHand',
    });
  },
};

/** „DURING COMBAT: Ignore the value of your opponent's card. AFTER COMBAT: Take 2 damage." */
const fifteenThousandPounds: CardEffect = {
  duringChoice: (e) => {
    const combat = e.ctx.state.combat;
    if (combat) (combat.flags ??= []).push(e.role === 'attacker' ? 'zeroDefense' : 'zeroAttack');
  },
  after: (e) => hurtRex(e, 2),
};

/** „IMMEDIATELY: Fighters cannot leave their spaces for the rest of the turn." */
export const terrifyingRoar: CardEffect = {
  immediate: (e) => {
    e.ctx.state.turn.rooted = true;
    emit(e.ctx, { type: 'specialUsed', player: e.player, heroId: TREX, name: 'Terrifying Roar: do konca ťahu sa nikto nepohne' });
  },
};

/**
 * „Place T-Rex in any space in her zone. Then, each opponent with a fighter
 * adjacent to T-Rex discards 1 random card." Susedia až po položení.
 */
const ripplesInTheWater: CardEffect = {
  scheme: (e) => {
    const rex = rexOf(e.ctx, e.player);
    if (!rex) return;
    const map = getMap(e.ctx);
    askChoice(e.ctx, {
      kind: 'place',
      player: e.player,
      source: e.cardId,
      label: 'Ripples in the Water: polož T. Rexa na priestor v jej zóne',
      optional: false,
      fighterId: rex.id,
      spaces: map.spaces
        .map((s) => s.id)
        .filter((id) => shareZone(map, id, rex.spaceId) && !e.ctx.state.fighters.some((f) => f.spaceId === id)),
    });
    askChoice(e.ctx, {
      kind: 'auto',
      player: e.player,
      source: e.cardId,
      label: 'Ripples in the Water: odhadzovanie',
      optional: false,
      follow: { kind: 'effect', effect: RIPPLES, data: 'discard' },
    });
  },
  onFollow: (ctx, choice) => {
    const rex = rexOf(ctx, choice.player);
    if (!rex) return;
    const around = new Set(neighbors(getMap(ctx), rex.spaceId));
    const victims = new Set(
      ctx.state.fighters.filter((f) => areEnemies(ctx.state, f.owner, choice.player) && around.has(f.spaceId)).map((f) => f.owner),
    );
    for (const player of victims) discardRandom(ctx, player, true);
  },
};

/** „DURING: You may BOOST this card. AFTER: If you won the combat, return this card to your hand." */
const youreJustMakingHerAngry: CardEffect = {
  duringChoice: (e) => askBoost(e, "You're Just Making Her Angry: smieš boostovať"),
  after: (e) => {
    if (!e.won) return;
    askChoice(e.ctx, {
      kind: 'auto',
      player: e.player,
      source: e.cardId,
      label: "You're Just Making Her Angry: karta späť do ruky",
      optional: false,
      follow: { kind: 'effect', effect: ANGRY, data: e.cardId },
    });
  },
  onFollow: (ctx, choice, cardId) => returnFromDiscard(ctx, choice.player, [cardId]),
};

export const tRexCards: EffectRegistry = {
  't-rex.closer-than-she-appears': closerThanSheAppears,
  't-rex.thrash': thrash,
  't-rex.reckless-lunge': recklessLunge,
  't-rex.when-dinosaurs-ruled-the-earth': whenDinosaursRuled,
  [GUT]: gutInstinct,
  't-rex.15000-pounds-of-muscle': fifteenThousandPounds,
  't-rex.terrifying-roar': terrifyingRoar,
  [RIPPLES]: ripplesInTheWater,
  't-rex.commanding-impact': draw(1),
  't-rex.momentous-shift': momentousShift,
  [ANGRY]: youreJustMakingHerAngry,
};

export const tRexSpecial: HeroSpecial = {
  name: 'Veľká figúrka',
  /** „At the end of your turn, draw a card." — pred odhadzovaním na limit ruky. */
  endOfTurn: (ctx, player) => {
    drawCard(ctx, player);
  },
};

